<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';

import { eqsApi, type IssueEditRights } from '@/api/eqs';
import IssueDashboard from '@/components/IssueDashboard.vue';
import IssueEscalateDialog from '@/components/IssueEscalateDialog.vue';
import StatusChip from '@/components/StatusChip.vue';
import type { ModuleRecord } from '@/types/domain';
import { notify } from '@/services/notice';
import { errorMessage, formatCell, formatDate } from '@/utils/format';
import { type InlineField, issueStatusOptions } from '@/utils/issue-status';
import {
  ISSUE_LEVELS, escalationAdvice, issueLevelLabel, issueLevelTone, recordLevel,
} from '@/utils/issue-escalation';

/*
  canEdit 必须写默认值：Vue 对 Boolean 类型的 prop 有 casting——
  不传时拿到的是 false 而不是 undefined，会把就地编辑整个关掉。
*/
const props = withDefaults(defineProps<{
  items: ModuleRecord[];
  projectId?: string | number | null;
  /** 只读账号不给就地编辑，单元格仍然照常显示 */
  canEdit?: boolean;
  /** 责任人候选：项目通讯录，输入时给提示但不限制 */
  ownerOptions?: string[];
  /**
   * 拿不到这一行的权限表时的兜底：质量口能关所有问题。
   * 有权限表时按行判（质量 / 本项目项目经理 / 提出人，0928），见 statusChoices。
   */
  canCloseIssues?: boolean;
  /**
   * 全局问题页复用本组件时显示「关联项目」列（项目内没必要）。
   * 两个清单原来是两套实现，字段、操作、口径各长各的（Tony 2026-08-18 指出不一致）；
   * 统一成这一个组件后，差异只剩这一列。
   */
  showProject?: boolean;
  /** project_id → 「DH25017 · X04-C出风口」，由全局页传入 */
  projectLabels?: Record<string, string>;
  /**
   * 每条问题「我能改什么」，key 是问题 ID（0823 Tony 拍板的分层）。
   *
   * 内容：质量 / 项目经理 / 提出者 / 责任人；
   * 状态与实际完成时间：质量 / 项目经理 / 提出者——**责任人不在这一档**。
   * 这里只负责灰掉，拦截在后端；表拿不到时按「能点」处理，见 editable()。
   */
  editRights?: Record<string, IssueEditRights>;
}>(), {
  projectId: null,
  canEdit: true,
  ownerOptions: () => [],
  canCloseIssues: false,
  showProject: false,
  projectLabels: () => ({}),
  editRights: () => ({}),
});

function projectLabelOf(item: ModuleRecord): string {
  const id = String(item.project_id ?? '');
  return id ? (props.projectLabels[id] ?? `项目 ${id}`) : '待归属';
}

const emit = defineEmits<{
  edit: [item: ModuleRecord];
  create: [];
  /** 打开 AI 立单 / 会议纪要导入对话框 */
  assist: [];
  /** 催办：给责任人推站内 + 邮件提醒 */
  remind: [item: ModuleRecord];
  /** 表格底部直接落一行；父级负责写库并刷新 */
  quickCreate: [draft: Record<string, string>];
  /** 这条问题已经有 8D，跳去 8D 页看 */
  open8d: [item: ModuleRecord];
  /** 单元格就地改；父级负责写库并把这一条换掉 */
  inlineUpdate: [payload: { item: ModuleRecord; field: InlineField; value: string }];
  /** 升级 / 降级已写库；父级把这一条换成返回的记录 */
  escalated: [saved: ModuleRecord];
  /**
   * 屏幕上当前这几行（筛完排完的）。
   *
   * 0916：模块中心那颗「导出当前结果 (N)」原来数的是它自己那套 dashboardFilter，
   * 和这张表看到的根本不是一批——在这儿筛到 18 条，导出仍是 50 条。
   * 把可见行报上去，导出跟着屏幕走。
   */
  viewChange: [rows: ModuleRecord[]];
}>();

/**
 * 单元格就地编辑。
 *
 * 只开放改得最勤的六列——责任人、等级、分类、当前计划、状态、进展。
 * 其余仍走抽屉：13 列全做成输入框，列会挤成一团，而零件号、根因、
 * 措施那些本来就要慢慢写，塞进一行反而更难填（试用反馈 0818）。
 *
 * 进展是**追加**不是覆盖：它是一条流水日志，列表里只露最新一条，
 * 就地编辑要是改成覆盖，一点就把之前的记录全冲了。
 */
const editing = ref<{ id: string; field: InlineField } | null>(null);
const editValue = ref('');

/*
  用函数 ref 而不是模板 ref 名：这些控件在 v-for 里，
  模板 ref 会被收集成数组，cellInput.value.focus() 根本不是函数——
  结果就是点完格子光标没进去，还得再点一次才能打字。
*/
function focusCell(el: unknown): void {
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.focus();
}

/**
 * 「状态 / 实际完成时间」这一档的列——责任人改不了这一档。
 * 实际完成时间不在清单的就地编辑里（只在抽屉），所以这里只有状态。
 */
const STATUS_TIER_FIELDS = new Set<InlineField>(['status']);

function rightsOf(item: ModuleRecord): IssueEditRights | null {
  return props.editRights[String(item.id)] ?? null;
}

/**
 * 这一格能不能点。
 *
 * 权限表还没回来、或者接口挂了时**按能点处理**：后端仍然会拦并给出「谁能改」，
 * 而整张表灰成一片、点了又没有任何解释，人只会认为系统坏了。
 * 前端这一层是省一次白点，不是安全边界。
 */
function editable(item: ModuleRecord, field: InlineField): boolean {
  if (!props.canEdit) return false;
  const rights = rightsOf(item);
  if (!rights) return true;
  return STATUS_TIER_FIELDS.has(field) ? rights.canEditStatus : rights.canEditContent;
}

/** 灰掉的格子鼠标悬上去要说得出谁能改，不能只是点不动 */
function lockedHint(item: ModuleRecord, field: InlineField): string {
  const rights = rightsOf(item);
  if (!rights || editable(item, field)) return '';
  const reporter = rights.foundBy?.trim() || '未填写';
  if (STATUS_TIER_FIELDS.has(field)) {
    return `「状态」责任人改不了：只有质量、项目经理和问题提出者（${reporter}）能改。`
      + '处理完了写一条进展，请他们确认。';
  }
  return `这条问题你改不了：能改的是质量、项目经理、问题提出者（${reporter}）`
    + `和责任人（${rights.ownerName?.trim() || '未指定'}）。`;
}

function isEditing(item: ModuleRecord, field: InlineField): boolean {
  return editing.value?.id === String(item.id) && editing.value.field === field;
}

function beginEdit(item: ModuleRecord, field: InlineField, current: string): void {
  if (!editable(item, field)) return;
  editing.value = { id: String(item.id), field };
  // 进展是追加，开局留空，不要让人以为要在旧文字上改
  // 值由 focusCell 在元素挂载时接手聚焦
  editValue.value = field === 'progress' ? '' : current;
}

function cancelEdit(): void {
  editing.value = null;
  editValue.value = '';
}

/** 下拉选完即存；输入框回车或失焦才存，值没变就当没点过 */
function commitEdit(item: ModuleRecord, field: InlineField, previous: string): void {
  const next = editValue.value.trim();
  cancelEdit();
  if (field === 'progress' ? !next : next === previous.trim()) return;
  emit('inlineUpdate', { item, field, value: next });
}

/*
  下拉/日期选完即存，值从事件目标上读——不依赖 v-model 和 @change 谁先跑：
  两个都挂在 change 上，顺序由 Vue 内部决定，靠 editValue 会读到上一次的值。
*/
function commitPicked(item: ModuleRecord, field: InlineField, previous: string, event: Event): void {
  editValue.value = (event.target as HTMLSelectElement | HTMLInputElement).value;
  commitEdit(item, field, previous);
}

/*
  关闭类状态给不给，按这一行判（0928 Tony：「我需要提出人和项目经理可以关闭」）。
  原来用的是全局那个「是不是质量」：提出人、项目经理在下拉里只看得到「处理中 / 受阻」。
  现在和「能不能改状态」同一档——质量 / 本项目项目经理 / 提出人，责任人不在里面。
*/
function statusChoices(item: ModuleRecord): Array<{ value: string; label: string }> {
  const rights = rightsOf(item);
  return issueStatusOptions(String(item.status || 'open'), rights ? rights.canEditStatus : props.canCloseIssues);
}

/**
 * 分类下拉的选项。
 *
 * 字典里只配了 4 个，生产库里实际在用的有 7 个——所以基础集直接取实际值，
 * 再并上这个项目里已经出现过的分类（各项目自己造过的词也能选到）。
 * 光靠「表里出现过的」不行：新项目一条问题都没有时下拉会是空的，
 * 第一条问题永远分不了类。
 */
const BASE_CATEGORIES = ['产品问题', '模具问题', '工装问题', '工艺问题', '项目问题', '设计缺陷', '审核问题'];
const categories = computed(() => [...new Set([
  ...BASE_CATEGORIES,
  ...props.items.map((item) => String(field(item, 'category') || '').trim()).filter(Boolean),
])].sort());

/**
 * 表格底部直接加一行。
 *
 * 原来记一条问题要开抽屉、填十几个字段、保存、关掉——而现场记问题时
 * 手上真正有的就是「谁、什么问题、什么时候要好」（试用反馈 0818）。
 * 这里只收四个字段落一行，剩下的等有空了点进去补。
 *
 * 这一行不代表「问题记全了」：保存后横幅会提示还能补哪些，
 * 不让人误以为四个字段就是一条完整的问题记录。
 */
const quickDraft = ref({ title: '', ownerName: '', dueAt: '', severityGrade: '' });

/*
  转 8D：为这条问题建一套 D0–D8。
  幂等由后端保证（已有步骤的原样返回），所以重复点不会建两套。
  建完把 has8d 就地置上，不用整页刷新。
*/
function start8d(item: ModuleRecord): void {
  /*
    8D 现在就在这条问题的抽屉里填（Tony 2026-08-18 定的方向）。
    清单上这个按钮只负责**把抽屉打开**，建步骤和填写都在抽屉里完成——
    原来「在清单上转、去另一个页面填」把一件事切成两处，人绕不回去。
  */
  emit('edit', item);
}
const quickSaving = ref(false);
// 0911 起新建必须定级：后端会拦，这里先不让按钮亮
const quickReady = computed(() => quickDraft.value.title.trim().length > 0 && Boolean(quickDraft.value.severityGrade));

async function submitQuick(): Promise<void> {
  if (!quickReady.value || quickSaving.value) return;
  quickSaving.value = true;
  try {
    emit('quickCreate', {
      title: quickDraft.value.title.trim(),
      description: quickDraft.value.title.trim(),
      ownerName: quickDraft.value.ownerName.trim(),
      dueAt: quickDraft.value.dueAt,
      severityGrade: quickDraft.value.severityGrade,
    });
    quickDraft.value = { title: '', ownerName: '', dueAt: '', severityGrade: '' };
  } finally {
    quickSaving.value = false;
  }
}

const query = ref('');
const status = ref('open');
/** 按列表显示的问题等级筛（severityGrade）；'__none__' 是没定级的那批 */
const gradeFilter = ref('');
const owner = ref('');
const grade = ref('');
const category = ref('');
const aging = ref('');
/** 图表默认收起（1027 UI 审计）：进页面先看清单，统计要看再点开 */
const showDashboard = ref(false);

/**
 * 附件缩略图。
 *
 * 旧 EQS 的问题清单能直接看到照片——不用点开就知道问题长什么样，
 * 这是那张表最实用的一点。文件早就在 203 的 legacy-public 里（68 个里 57 个是图片）。
 *
 * 按需加载：一屏几十条，进页面就把每条的附件都拉一遍是几十个请求，
 * 而多数问题根本没有附件。
 */
interface Attachment {
  index: number;
  kind: 'description' | 'evidence';
  name: string;
  isImage: boolean;
}
const attachments = ref<Map<string, Attachment[]>>(new Map());

/**
 * 预览面板里的一份文件。
 *
 * kind 决定用什么壳子显示：图片用 <img>、PDF 用 <iframe>，
 * 其余（zip / xlsx / docx…）只给下载——inline 出来是一屏乱码，
 * 后端也只对图片和 PDF 认 inline=1（module-tools.controller 的 PREVIEWABLE）。
 */
interface PreviewFile {
  name: string;
  /** 能直接看的用它当 src；看不了的为空 */
  previewUrl: string;
  downloadUrl: string;
  kind: 'image' | 'pdf' | 'other';
}
const preview = ref<{ title: string; files: PreviewFile[]; index: number } | null>(null);
const previewLoadingId = ref('');
const currentPreview = computed<PreviewFile | null>(() =>
  preview.value?.files[preview.value.index] ?? null);

/*
  判类型的规则和 RecordDrawer 里那份一致：先看 MIME，老数据没有 MIME 就看扩展名。
  两处各写一份是已知的重复，抽公共 util 要动 RecordDrawer，不在这次的改动范围里。
*/
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/iu;
function attachmentKind(file: { mimeType?: string; fileName: string }): PreviewFile['kind'] {
  const mime = (file.mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/') || IMAGE_EXT.test(file.fileName)) return 'image';
  if (mime === 'application/pdf' || /\.pdf$/iu.test(file.fileName)) return 'pdf';
  return 'other';
}

function attachmentsOf(item: ModuleRecord): Attachment[] {
  return attachments.value.get(String(item.id)) ?? [];
}

/** 迁移时把附件路径原样留在了 normalized 里，据此判断值不值得去取 */
function mayHaveAttachments(item: ModuleRecord): boolean {
  const raw = `${field(item, 'legacyAttachments') ?? ''}${field(item, 'legacyEvidenceFiles') ?? ''}`;
  return Boolean(raw) && raw !== '[]' && raw !== '[][]';
}

/** 新附件域的计数（module_record_attachments）；旧 EQS 附件仍走下面的缩略图逻辑 */
const newAttachmentCounts = ref<Record<string, number>>({});
async function loadNewAttachmentCounts(): Promise<void> {
  if (!props.projectId) return;
  try {
    newAttachmentCounts.value = await eqsApi.moduleAttachmentCounts('issues', 'issues', props.projectId);
  } catch {
    newAttachmentCounts.value = {};
  }
}

async function loadAttachments(): Promise<void> {
  if (!props.projectId) return;
  const pending = props.items.filter((item) =>
    mayHaveAttachments(item) && !attachments.value.has(String(item.id)));
  if (!pending.length) return;
  const next = new Map(attachments.value);
  await Promise.all(pending.map(async (item) => {
    try {
      const payload = await eqsApi.issueAttachments(props.projectId as string | number, item.id);
      next.set(String(item.id), (payload.attachments ?? []) as unknown as Attachment[]);
    } catch {
      // 取不到就当没有，不因为附件挂了而影响整张表
      next.set(String(item.id), []);
    }
  }));
  attachments.value = next;
}

function attachmentUrl(item: ModuleRecord, index: number): string {
  return eqsApi.issueAttachmentUrl(props.projectId as string | number, item.id, index);
}

/** 旧 EQS 迁来的附件：只有一个直链，能不能看按 isImage 判 */
function openPreview(item: ModuleRecord, attachment: Attachment): void {
  const url = attachmentUrl(item, attachment.index);
  preview.value = {
    title: `${formatCell(item.code)} 的附件`,
    index: 0,
    files: [{
      name: attachment.name,
      previewUrl: attachment.isImage ? url : '',
      downloadUrl: url,
      kind: attachment.isImage ? 'image' : 'other',
    }],
  };
}

/**
 * 点「📎 N」直接看附件（0823 Tony）。
 *
 * 原来这个徽章只是个数字，要看图得先点开行、等抽屉、再点预览——
 * 而问题清单上最常做的事就是「这条到底长什么样」。
 * 附件清单按需拉：一屏几十条，进页面就全拉一遍是几十个请求。
 */
async function openAttachmentPreview(item: ModuleRecord): Promise<void> {
  const id = String(item.id);
  if (previewLoadingId.value) return;
  previewLoadingId.value = id;
  try {
    const payload = await eqsApi.moduleAttachments('issues', 'issues', item.id);
    const files: PreviewFile[] = (payload.attachments ?? []).map((file) => ({
      name: file.fileName,
      kind: attachmentKind(file),
      previewUrl: attachmentKind(file) === 'other'
        ? ''
        : eqsApi.moduleAttachmentPreviewUrl('issues', 'issues', item.id, file.id),
      downloadUrl: eqsApi.moduleAttachmentDownloadUrl('issues', 'issues', item.id, file.id),
    }));
    if (!files.length) {
      // 计数是进页面时拉的，附件可能刚被别人删掉——如实说，别弹个空框
      notify('info', '这条问题没有附件了', '可能刚被删除，刷新一下就对上了。');
      newAttachmentCounts.value = { ...newAttachmentCounts.value, [id]: 0 };
      return;
    }
    // 开在第一个能看的上；一份都看不了时也照样开，让人直接下载
    const first = files.findIndex((file) => file.kind !== 'other');
    preview.value = {
      title: `${formatCell(item.code)} 的附件`,
      files,
      index: first < 0 ? 0 : first,
    };
  } catch (caught) {
    notify('error', '附件打不开', errorMessage(caught));
  } finally {
    previewLoadingId.value = '';
  }
}

/**
 * 超期天数：当前计划日期过了多少天。
 * 和「问题天数」不是一回事——那个从发现日算起，这个只看拖了计划多久，
 * 旧 EQS 两列都有，后者才是催办用的。
 */
function overdueDays(item: ModuleRecord): number | null {
  const plan = planDate(item);
  if (!plan || terminalStatuses.has(String(item.status || ''))) return null;
  const days = Math.floor((Date.parse(today()) - Date.parse(plan)) / 86_400_000);
  return days > 0 ? days : null;
}

watch(() => [props.items, props.projectId], () => { void loadAttachments(); void loadNewAttachmentCounts(); }, { immediate: true });

const terminalStatuses = new Set(['closed', 'completed', 'approved', 'cancelled']);

function normalized(item: ModuleRecord): Record<string, unknown> {
  return item.normalized && typeof item.normalized === 'object'
    ? item.normalized as Record<string, unknown>
    : {};
}

function field(item: ModuleRecord, key: string): unknown {
  return item[key] ?? normalized(item)[key];
}

function display(value: unknown, fallback: string): string {
  const formatted = formatCell(value);
  return formatted === '—' ? fallback : formatted;
}

function ownerName(item: ModuleRecord): string {
  return display(item.owner_name || item.ownerName || field(item, 'ownerName'), '未指定');
}

function dueDate(item: ModuleRecord): string {
  return String(item.due_at || item.dueAt || '').slice(0, 10);
}

/** 当前计划日期优先，没填才回落到「要求完成」——旧 LOP 里改期改的就是前者 */
function planDate(item: ModuleRecord): string {
  return String(field(item, 'currentPlanDate') || dueDate(item) || '').slice(0, 10);
}

function isOverdue(item: ModuleRecord): boolean {
  const plan = planDate(item);
  return Boolean(plan && !terminalStatuses.has(String(item.status || '')) && plan < today());
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * 问题天数：发现日 → 完成日；未完成的算到今天。
 * 旧 EQS 列表里有这一列，是「这个问题拖了多久」最直接的指标。
 */
function ageDays(item: ModuleRecord): number | null {
  const found = String(field(item, 'foundAt') || item.created_at || '').slice(0, 10);
  if (!found) return null;
  const end = String(field(item, 'completedAt') || '').slice(0, 10) || today();
  const days = Math.floor((Date.parse(end) - Date.parse(found)) / 86_400_000);
  return Number.isFinite(days) ? Math.max(0, days) : null;
}

function agingBucket(item: ModuleRecord): string {
  const age = ageDays(item);
  if (age === null) return '';
  if (age <= 7) return '0-7';
  if (age <= 30) return '8-30';
  if (age <= 90) return '31-90';
  return '90+';
}

/** 只取最新一条进展摆在列表里；完整日志在编辑抽屉看 */
function latestProgress(item: ModuleRecord): string {
  const log = String(field(item, 'progressLog') || '').trim();
  if (!log) return '';
  return log.split(/\r?\n/u).map((line) => line.trim()).filter(Boolean)[0] ?? '';
}

function gradeOf(item: ModuleRecord): string {
  return String(field(item, 'severityGrade') || '');
}

/*
  升级层级（0911）：1 工程师 → 2 项目经理与核心组 → 3 部门负责人 → 4 管理层。
  列表上以「谁在扛」为主标签；「建议升级」按逾期 7 天 / 催过 2 次的判据亮标记，
  系统只建议、人拍板（点「升级」）。
*/
const levelFilter = ref('');

function levelOf(item: ModuleRecord): number {
  return recordLevel(item);
}

function adviceOf(item: ModuleRecord): string {
  return escalationAdvice({
    level: item.escalation_level,
    open: !terminalStatuses.has(String(item.status || '')),
    planDate: planDate(item),
    today: today(),
    remindersSinceLevel: Number(item.reminders_since_level ?? 0),
  });
}

/**
 * 升级弹窗：0923 抽成 IssueEscalateDialog，升级问题看板用的是同一个。
 * 这里只管开哪一条、写完之后把这一条交给父级就地替换。
 */
const escalateItem = ref<ModuleRecord | null>(null);

function openEscalate(item: ModuleRecord): void {
  escalateItem.value = item;
}

function drill(filter: { kind: string; value: string }): void {
  // 图表点下去要能落到列表，否则只是好看
  status.value = 'all';
  /*
    顶上那排数字也是筛选入口（0916）。原来清单上方另有一排一模一样的磁贴
    （全部 / 待处理 / 已逾期 / 未指定责任人）专门干这个，同一组数字摆两遍，
    而且两排的判据还各写各的。磁贴撤掉，入口并到读数上。
  */
  if (filter.kind === 'kpi') {
    clearDrill();
    owner.value = '';
    levelFilter.value = '';
    if (filter.value === 'open') status.value = 'open';
    if (filter.value === 'overdue') status.value = 'overdue';
    if (filter.value === 'escalated') levelFilter.value = 'up';
    if (filter.value === 'unassigned') owner.value = '未指定';
    if (filter.value === 'noplan') { status.value = 'open'; noPlanOnly.value = true; }
    if (filter.value !== 'noplan') noPlanOnly.value = false;
    return;
  }
  if (filter.kind === 'status') status.value = filter.value === 'closed' ? 'closed' : 'all';
  if (filter.kind === 'grade') grade.value = filter.value;
  if (filter.kind === 'category') category.value = filter.value;
  if (filter.kind === 'owner') owner.value = filter.value;
  if (filter.kind === 'aging') aging.value = filter.value;
  if (filter.kind === 'level') levelFilter.value = filter.value;
}

function clearDrill(): void {
  grade.value = '';
  category.value = '';
  aging.value = '';
  noPlanOnly.value = false;
}

const owners = computed(() => [...new Set(props.items.map(ownerName).filter((name) => name !== '未指定'))].sort());

/** 「未设完成日期」那一格点下来的（0916）：没有日期就永远不会逾期，判据全程空转 */
const noPlanOnly = ref(false);

/*
  排序（0916 Tony:「能不能按照优先级或者严重性来做排序」）。

  原来一个 sort 都没有——列表就是接口返回的顺序（按 id），50 条问题里
  A 级严重的和 D 级轻微的随机穿插，人得自己一行行找。

  默认「最该先看」按三层排，理由各不相同：
    ① 未关闭的永远在前 —— 已关闭的是复盘材料，不该占着屏幕上半页；
    ② 严重性 A → B → C → D → 未定级 —— 这是 Tony 点名的那把尺子；
       **未定级排在最后而不是最前**：它不是"最不严重"，是"还没人定"，
       混在 A 前面会把真正的 A 挤下去，而它该做的事是被定级，不是被抢先处理；
    ③ 同级里逾期久的在前，都不逾期就按升级层级 —— 升到管理层的那几条，
       层级本身就是「已经有人在催了」的信号。
  其余几档是给不同人用的：质量口看逾期，项目经理看升级层级，日常跟进看最近更新。
*/
type SortKey = 'smart' | 'grade' | 'overdue' | 'level' | 'recent';
const sortBy = ref<SortKey>('smart');

/** A=0 … D=3，未定级 99（排最后，它是数据缺口不是轻微） */
function gradeRank(item: ModuleRecord): number {
  const grade = (gradeOf(item) || '').trim();
  const index = ['A', 'B', 'C', 'D'].indexOf(grade.charAt(0).toUpperCase());
  return index < 0 ? 99 : index;
}

/** 排序用的逾期天数：复用上面那个 overdueDays（它对没逾期/没日期的返回 null） */
function lateDays(item: ModuleRecord): number {
  return Math.max(0, overdueDays(item) ?? 0);
}

function updatedAt(item: ModuleRecord): number {
  const raw = String(item.updated_at ?? item.updatedAt ?? item.created_at ?? '');
  const time = Date.parse(raw);
  return Number.isFinite(time) ? time : 0;
}

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'smart', label: '最该先看（未关闭 → 严重性 → 逾期）' },
  { value: 'grade', label: '按严重性 A → D' },
  { value: 'overdue', label: '按逾期天数' },
  { value: 'level', label: '按升级层级' },
  { value: 'recent', label: '最近更新' },
];

function compare(a: ModuleRecord, b: ModuleRecord): number {
  const openA = terminalStatuses.has(String(a.status || '')) ? 1 : 0;
  const openB = terminalStatuses.has(String(b.status || '')) ? 1 : 0;
  if (sortBy.value !== 'recent' && openA !== openB) return openA - openB;
  if (sortBy.value === 'grade') return gradeRank(a) - gradeRank(b) || lateDays(b) - lateDays(a);
  if (sortBy.value === 'overdue') return lateDays(b) - lateDays(a) || gradeRank(a) - gradeRank(b);
  if (sortBy.value === 'level') return levelOf(b) - levelOf(a) || gradeRank(a) - gradeRank(b);
  if (sortBy.value === 'recent') return updatedAt(b) - updatedAt(a);
  // smart
  return gradeRank(a) - gradeRank(b)
    || lateDays(b) - lateDays(a)
    || levelOf(b) - levelOf(a);
}

const filteredItems = computed(() => {
  const keyword = query.value.trim().toLocaleLowerCase();
  return props.items.filter((item) => {
    const currentStatus = String(item.status || 'open');
    if (status.value === 'open' && terminalStatuses.has(currentStatus)) return false;
    if (status.value === 'closed' && !terminalStatuses.has(currentStatus)) return false;
    if (status.value === 'overdue' && !isOverdue(item)) return false;
    if (gradeFilter.value === '__none__') {
      if (gradeOf(item)) return false;
    } else if (gradeFilter.value && gradeOf(item) !== gradeFilter.value) return false;
    if (owner.value && ownerName(item) !== owner.value) return false;
    if (levelFilter.value === 'up') {
      if (levelOf(item) < 3) return false;
    } else if (levelFilter.value === 'suggest') {
      if (!adviceOf(item)) return false;
    } else if (levelFilter.value && String(levelOf(item)) !== levelFilter.value) return false;
    if (grade.value && (gradeOf(item) || '未定级') !== grade.value) return false;
    if (category.value && (String(field(item, 'category') || '') || '未分类') !== category.value) return false;
    if (aging.value && (agingBucket(item) !== aging.value || terminalStatuses.has(currentStatus))) return false;
    if (noPlanOnly.value && planDate(item)) return false;
    if (!keyword) return true;
    return [
      item.code,
      item.title,
      item.status,
      item.priority,
      ownerName(item),
      field(item, 'description'),
      field(item, 'category'),
      field(item, 'occurrenceStage'),
      field(item, 'partNumber'),
      field(item, 'rootCause'),
      field(item, 'countermeasure'),
      field(item, 'progressLog'),
    ].filter(Boolean).join(' ').toLocaleLowerCase().includes(keyword);
  });
});

const sortedItems = computed(() => [...filteredItems.value].sort(compare));

// 可见行变了就报给父级：导出、条数提示都跟着屏幕走，不各算各的
watch(sortedItems, (rows) => emit('viewChange', rows), { immediate: true });

const drillActive = computed(() => Boolean(
  grade.value || category.value || aging.value || noPlanOnly.value,
));

const counts = computed(() => ({
  total: props.items.length,
  open: props.items.filter((item) => !terminalStatuses.has(String(item.status || ''))).length,
  overdue: props.items.filter(isOverdue).length,
  unassigned: props.items.filter((item) => ownerName(item) === '未指定').length,
}));

function priorityLabel(value: unknown): string {
  const labels: Record<string, string> = {
    normal: '普通',
    high: '高',
    critical: '紧急',
  };
  return labels[String(value || '')] || display(value, '未分级');
}

function statusLabel(item: ModuleRecord): string {
  if (isOverdue(item)) return '已逾期';
  const labels: Record<string, string> = {
    open: '待处理',
    pending: '待处理',
    in_progress: '处理中',
    blocked: '受阻',
    closed: '已关闭',
    completed: '已完成',
    approved: '已通过',
    cancelled: '已取消',
  };
  return labels[String(item.status || '')] || display(item.status, '未定义');
}
</script>

<template>
  <section class="issue-workspace" aria-label="问题清单">
    <header>
      <div>
        <!--
          全局清单（模块中心）外面那页已经有「问题列表」这个标题了，这里再写一遍
          「项目问题清单」是同一件事的第二个名字，白占一屏（0916 布局收拾）。
          项目页里没有外层标题，照旧显示。操作提示两处都留——它说的是这张表怎么用。
          英文眉标「PROJECT ISSUE LIST」1027 撤掉，只留中文标题。
        -->
        <h2 v-if="!showProject">项目问题清单</h2>
        <p>
          分类、等级、责任人、当前计划、进展、状态这六列<b>点一下就能直接改</b>；
          零件号、根因、措施这些点问题行进详情写。
        </p>
      </div>
      <button class="create-issue" type="button" @click="emit('create')">＋ 新建并指定责任人</button>
      <button class="create-issue assist-entry" type="button" @click="emit('assist')">✨ AI 立单 / 纪要导入</button>
    </header>

    <div class="issue-filters">
      <label class="issue-search">
        <span>快速查找</span>
        <input v-model="query" type="search" placeholder="问题编号、描述、责任人、零件号">
      </label>
      <label>
        <span>状态</span>
        <select v-model="status">
          <option value="open">待处理</option>
          <option value="all">全部状态</option>
          <option value="overdue">已逾期</option>
          <option value="closed">已关闭</option>
        </select>
      </label>
      <label>
        <!--
          筛的必须是列表上显示的那个字段。
          原来这里筛 priority（critical/high/normal），而列上显示的是 severityGrade
          （A-严重/B-重要…）——两个字段。更糟的是库里 priority 只有 normal(94) 和「高」(3)，
          选「紧急」「高」一条都筛不出来，等于这个筛选条一直是坏的（2026-08-18 查实）。
        -->
        <span>问题等级</span>
        <select v-model="gradeFilter">
          <option value="">全部等级</option>
          <option value="A-严重">A-严重</option>
          <option value="B-重要">B-重要</option>
          <option value="C-一般">C-一般</option>
          <option value="D-轻微">D-轻微</option>
          <option value="__none__">未定级</option>
        </select>
      </label>
      <label>
        <span>升级层级</span>
        <select v-model="levelFilter">
          <option value="">全部层级</option>
          <option value="up">已升到部门 / 管理层</option>
          <option value="suggest">建议升级</option>
          <option v-for="lv in ISSUE_LEVELS" :key="lv.level" :value="String(lv.level)">{{ lv.label }}</option>
        </select>
      </label>
      <label>
        <span>责任人</span>
        <select v-model="owner">
          <option value="">全部责任人</option>
          <option value="未指定">未指定</option>
          <option v-for="name in owners" :key="name" :value="name">{{ name }}</option>
        </select>
      </label>
      <!-- 0916 Tony:「能不能按照优先级或者严重性来做排序」。原来一个 sort 都没有 -->
      <label class="issue-sort">
        <span>排序</span>
        <select v-model="sortBy">
          <option v-for="option in SORT_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </label>
      <b>{{ sortedItems.length }} 项</b>
    </div>

    <div class="issue-table-wrap">
      <table :class="{ 'can-edit': canEdit }">
        <thead>
          <tr>
            <th>问题编号</th>
            <th class="col-desc">问题描述</th>
            <th v-if="showProject">关联项目</th>
            <th class="col-category">分类 / 阶段</th>
            <th>附件</th>
            <th class="col-grade">等级</th>
            <th class="col-owner">责任人</th>
            <th class="col-owner">提出人</th>
            <th>发现日期</th>
            <th class="col-plan">当前计划</th>
            <th>问题天数</th>
            <th>超期</th>
            <!-- 原因/措施合并一列：分开两列的话 400 字的根因会把表格挤出屏（0819 反馈要求补这两项） -->
            <th class="col-cause">原因 / 措施</th>
            <th class="col-progress">最新进展</th>
            <th class="col-status">状态</th>
            <th class="col-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in sortedItems"
            :key="item.id"
            :class="{
              overdue: isOverdue(item),
              'grade-a': gradeOf(item).startsWith('A'),
              'is-closed': terminalStatuses.has(String(item.status || '')),
            }"
            tabindex="0"
            @click="emit('edit', item)"
            @keydown.enter="emit('edit', item)"
          >
            <td><b>{{ formatCell(item.code) }}</b></td>
            <td>
              <strong>{{ formatCell(item.title) }}</strong>
              <small>{{ display(field(item, 'description'), '未填写问题描述') }}</small>
            </td>
            <td v-if="showProject" class="issue-project" @click.stop>
              <RouterLink
                v-if="item.project_id"
                :to="`/projects/${item.project_id}/issues`"
              >{{ projectLabelOf(item) }}</RouterLink>
              <span v-else class="issue-muted">待归属</span>
            </td>
            <td
              class="cell-edit"
              :class="{ 'is-editing': isEditing(item, 'category'), locked: !editable(item, 'category') }"
              :title="lockedHint(item, 'category')"
              @click.stop="beginEdit(item, 'category', String(field(item, 'category') || ''))"
            >
              <select
                v-if="isEditing(item, 'category')"
                :ref="focusCell"
                v-model="editValue"
                @change="commitPicked(item, 'category', String(field(item, 'category') || ''), $event)"
                @blur="cancelEdit"
                @keydown.esc="cancelEdit"
              >
                <option value="">未分类</option>
                <option v-for="name in categories" :key="name" :value="name">{{ name }}</option>
              </select>
              <template v-else>
                {{ display(field(item, 'category'), '未分类') }}
                <small>{{ display(field(item, 'occurrenceStage'), '阶段未维护') }}</small>
              </template>
            </td>
            <td class="thumb-cell">
              <template v-if="attachmentsOf(item).length">
                <button
                  v-for="file in attachmentsOf(item).slice(0, 3)"
                  :key="file.index"
                  type="button"
                  class="thumb"
                  :class="{ 'is-file': !file.isImage, evidence: file.kind === 'evidence' }"
                  :title="`${file.kind === 'evidence' ? '证据附件' : '描述附件'}：${file.name}`"
                  @click.stop="openPreview(item, file)"
                >
                  <img v-if="file.isImage" :src="attachmentUrl(item, file.index)" :alt="file.name" loading="lazy">
                  <span v-else>{{ file.name.split('.').pop()?.toUpperCase() }}</span>
                </button>
                <small v-if="attachmentsOf(item).length > 3">+{{ attachmentsOf(item).length - 3 }}</small>
              </template>
              <!--
                新系统里传的附件（照片/文件）。
                0823 Tony：点徽章就要能看，不用先开抽屉——问题清单上最常做的事
                就是「这条到底长什么样」。非图片非 PDF 仍然只给下载。
              -->
              <button
                v-if="newAttachmentCounts[String(item.id)]"
                type="button"
                class="attach-badge"
                :disabled="previewLoadingId === String(item.id)"
                :title="`${newAttachmentCounts[String(item.id)]} 个附件，点击直接预览`"
                @click.stop="openAttachmentPreview(item)"
              >📎 {{ previewLoadingId === String(item.id) ? '…' : newAttachmentCounts[String(item.id)] }}</button>
              <small v-else-if="!attachmentsOf(item).length" class="missing">—</small>
            </td>
            <td
              class="cell-edit"
              :class="{ 'is-editing': isEditing(item, 'severityGrade'), locked: !editable(item, 'severityGrade') }"
              :title="lockedHint(item, 'severityGrade')"
              @click.stop="beginEdit(item, 'severityGrade', gradeOf(item))"
            >
              <select
                v-if="isEditing(item, 'severityGrade')"
                :ref="focusCell"
                v-model="editValue"
                @change="commitPicked(item, 'severityGrade', gradeOf(item), $event)"
                @blur="cancelEdit"
                @keydown.esc="cancelEdit"
              >
                <option value="">未定级</option>
                <option value="A-严重">A-严重</option>
                <option value="B-重要">B-重要</option>
                <option value="C-一般">C-一般</option>
                <option value="D-轻微">D-轻微</option>
              </select>
              <template v-else>
                <span class="priority" :class="String(item.priority || '')">
                  {{ gradeOf(item) || priorityLabel(item.priority) }}
                </span>
                <small
                  v-if="levelOf(item) >= 2"
                  class="level"
                  :class="`level-${issueLevelTone(item.escalation_level)}`"
                  :title="`这条问题现在在「${issueLevelLabel(item.escalation_level)}」层`"
                >{{ issueLevelLabel(item.escalation_level, true) }}</small>
                <small v-if="adviceOf(item)" class="escalate" :title="adviceOf(item)">建议升级</small>
              </template>
            </td>
            <td
              class="cell-edit"
              :class="{ 'is-editing': isEditing(item, 'ownerName'), locked: !editable(item, 'ownerName') }"
              :title="lockedHint(item, 'ownerName')"
              @click.stop="beginEdit(item, 'ownerName', ownerName(item) === '未指定' ? '' : ownerName(item))"
            >
              <input
                v-if="isEditing(item, 'ownerName')"
                :ref="focusCell"
                v-model="editValue"
                type="text"
                list="issue-owner-options"
                placeholder="责任人"
                @keydown.enter="commitEdit(item, 'ownerName', ownerName(item) === '未指定' ? '' : ownerName(item))"
                @blur="commitEdit(item, 'ownerName', ownerName(item) === '未指定' ? '' : ownerName(item))"
                @keydown.esc="cancelEdit"
              >
              <span v-else :class="{ missing: ownerName(item) === '未指定' }">{{ ownerName(item) }}</span>
            </td>
            <td class="proposer-cell">{{ field(item, 'foundBy') || '—' }}</td>
            <td>{{ formatDate(String(field(item, 'foundAt') || '')) }}</td>
            <td
              class="cell-edit"
              :class="{
                danger: isOverdue(item),
                'is-editing': isEditing(item, 'currentPlanDate'),
                locked: !editable(item, 'currentPlanDate'),
              }"
              :title="lockedHint(item, 'currentPlanDate')"
              @click.stop="beginEdit(item, 'currentPlanDate', planDate(item))"
            >
              <input
                v-if="isEditing(item, 'currentPlanDate')"
                :ref="focusCell"
                v-model="editValue"
                type="date"
                @change="commitPicked(item, 'currentPlanDate', planDate(item), $event)"
                @keydown.enter="commitEdit(item, 'currentPlanDate', planDate(item))"
                @keydown.esc="cancelEdit"
              >
              <template v-else>{{ formatDate(planDate(item)) }}</template>
            </td>
            <td class="age-cell">
              <b v-if="ageDays(item) !== null" :class="{ risk: (ageDays(item) ?? 0) > 90 }">{{ ageDays(item) }}</b>
              <span v-else>—</span>
            </td>
            <td class="overdue-cell">
              <b v-if="overdueDays(item) !== null">{{ overdueDays(item) }}</b>
              <span v-else>—</span>
            </td>
            <td class="cause-cell">
              <div v-if="field(item, 'rootCause')" class="cause-line" :title="String(field(item, 'rootCause'))">
                <i>因</i>{{ field(item, 'rootCause') }}
              </div>
              <div v-if="field(item, 'countermeasure')" class="cause-line" :title="String(field(item, 'countermeasure'))">
                <i>措</i>{{ field(item, 'countermeasure') }}
              </div>
              <small v-if="!field(item, 'rootCause') && !field(item, 'countermeasure')" class="missing">未分析</small>
            </td>
            <td
              class="progress-cell cell-edit"
              :class="{ 'is-editing': isEditing(item, 'progress'), locked: !editable(item, 'progress') }"
              :title="lockedHint(item, 'progress')"
              @click.stop="beginEdit(item, 'progress', '')"
            >
              <input
                v-if="isEditing(item, 'progress')"
                :ref="focusCell"
                v-model="editValue"
                type="text"
                placeholder="记一条进展，回车保存"
                @keydown.enter="commitEdit(item, 'progress', '')"
                @blur="commitEdit(item, 'progress', '')"
                @keydown.esc="cancelEdit"
              >
              <template v-else>
                <span v-if="latestProgress(item)">{{ latestProgress(item) }}</span>
                <small v-else class="missing">未记录进展</small>
              </template>
            </td>
            <!-- 状态属于「责任人改不了」那一档，灰掉时 title 会说清楚谁能改 -->
            <td
              class="cell-edit col-status"
              :class="{ 'is-editing': isEditing(item, 'status'), locked: !editable(item, 'status') }"
              :title="lockedHint(item, 'status')"
              @click.stop="beginEdit(item, 'status', String(item.status || ''))"
            >
              <select
                v-if="isEditing(item, 'status')"
                :ref="focusCell"
                v-model="editValue"
                @change="commitPicked(item, 'status', String(item.status || ''), $event)"
                @blur="cancelEdit"
                @keydown.esc="cancelEdit"
              >
                <option v-for="choice in statusChoices(item)" :key="choice.value" :value="choice.value">
                  {{ choice.label }}
                </option>
              </select>
              <StatusChip v-else :value="isOverdue(item) ? 'overdue' : item.status" :label="statusLabel(item)" />
            </td>
            <td class="issue-actions col-actions">
              <button type="button" @click.stop="emit('edit', item)">维护责任与详情 →</button>
              <button
                v-if="ownerName(item) !== '未指定'"
                type="button"
                class="remind-button"
                :title="`提醒 ${ownerName(item)} 处理（站内 + 邮件）`"
                @click.stop="emit('remind', item)"
              >催办</button>
              <button
                v-if="canEdit && !terminalStatuses.has(String(item.status || ''))"
                type="button"
                class="escalate-button"
                :class="{ suggested: Boolean(adviceOf(item)) }"
                :title="adviceOf(item) ? `建议升级：${adviceOf(item)}` : `现在在「${issueLevelLabel(item.escalation_level)}」层，点开选升到哪一级`"
                @click.stop="openEscalate(item)"
              >升级</button>
              <!--
                转 8D：需要走八步法的问题才建 D0–D8，不是每条问题都摆一套
                （Tony 2026-08-18 定的方向）。已经有 8D 的显示「查看 8D」。
              -->
              <button
                type="button"
                class="eightd-button"
                :title="item.has8d ? '打开这条问题，8D 步骤在抽屉里' : '打开这条问题，可在抽屉里建 D0–D8'"
                @click.stop="start8d(item)"
              >{{ item.has8d ? '8D ✓' : '8D' }}</button>
            </td>
          </tr>
          <!--
            底部快速加一行：现场记问题时手上只有「谁、什么问题、什么时候要好」，
            为这四个字段开一次抽屉太重（试用反馈 0818）。
          -->
          <tr class="issue-quick-row">
            <td class="quick-hint">＋ 新一行</td>
            <td v-if="showProject" class="issue-muted">按代号自动挂</td>
            <td>
              <input
                v-model="quickDraft.title"
                type="text"
                placeholder="问题描述（必填）"
                aria-label="新问题的描述"
                @keydown.enter="submitQuick"
              >
            </td>
            <td colspan="2" class="quick-muted">分类 / 阶段稍后补</td>
            <td>
              <select v-model="quickDraft.severityGrade" aria-label="新问题的等级" required :class="{ 'quick-missing': !quickDraft.severityGrade && quickDraft.title.trim() }">
                <option value="">等级（必选）</option>
                <option value="A-严重">A-严重</option>
                <option value="B-重要">B-重要</option>
                <option value="C-一般">C-一般</option>
                <option value="D-轻微">D-轻微</option>
              </select>
            </td>
            <td>
              <!--
                和上面就地编辑那一格接同一份项目成员名单（0823 第 8 条）。
                少接这一行的后果不是「不好用」，是新问题的责任人写法自己长一套：
                这里手打「张昌绪 」带个空格，催办和「未指定责任人」的计数就都对不上他。
              -->
              <input
                v-model="quickDraft.ownerName"
                type="text"
                list="issue-owner-options"
                placeholder="责任人"
                aria-label="新问题的责任人"
                @keydown.enter="submitQuick"
              >
            </td>
            <!--
              提出人这一格空着，但**必须占位**。少了它，后面每一格都往左串一列：
              「今天」顶在提出人下面、日期框顶在发现日期下面、「保存这一行」跑到最新进展下面，
              最后两列空着（0824 量出来：表头 15 列，这一行只铺了 13 格）。
              表格错位不会报错，只会让人照着错的列去填。
            -->
            <td class="quick-muted"></td>
            <td class="quick-muted">今天</td>
            <td>
              <input v-model="quickDraft.dueAt" type="date" aria-label="新问题的要求完成日期">
            </td>
            <td colspan="5" class="quick-muted">保存后可点进去补齐分类、零件号、根因和措施</td>
            <td class="issue-actions col-actions">
              <button
                type="button"
                class="quick-save"
                :disabled="!quickReady || quickSaving"
                @click="submitQuick"
              >{{ quickSaving ? '保存中…' : '保存这一行' }}</button>
            </td>
          </tr>
        </tbody>
      </table>

      <datalist id="issue-owner-options">
        <option v-for="name in ownerOptions" :key="name" :value="name" />
      </datalist>

      <button v-if="!sortedItems.length" class="issue-empty" type="button" @click="emit('create')">
        <b>当前筛选下没有问题记录</b>
        <span>可调整筛选条件，或新建并指定责任人。</span>
      </button>
    </div>

    <!--
      这里原来还有一排磁贴（全部 / 待处理 / 已逾期 / 未指定责任人），和下面看板顶上
      那排读数是同一组数字、同一套筛选，摆两遍（0916 Tony:「问题清单模块怎么有两个
      dashboard」）。磁贴撤掉，点击并到看板那排数字上——读数和入口是一件事。

      1027 UI 审计：图表挪到清单后面、默认收起。进页面第一屏是要处理的问题，
      统计是第二位的；展开了也排在表后，不把清单顶下去。
    -->
    <div class="dashboard-toggle">
      <button type="button" @click="showDashboard = !showDashboard">
        {{ showDashboard ? '收起图表' : '查看图表' }}
      </button>
      <span v-if="drillActive" class="drill-note">
        图表筛选生效中
        <button type="button" class="drill-clear" @click="clearDrill">清除</button>
      </span>
    </div>
    <IssueDashboard v-if="showDashboard" :items="items" @drill="drill" />

    <div v-if="preview" class="preview-mask" @click.self="preview = null">
      <figure class="preview-box">
        <img
          v-if="currentPreview?.kind === 'image'"
          :src="currentPreview.previewUrl"
          :alt="currentPreview.name"
        >
        <!-- PDF 用 iframe：后端带 inline=1 时才不会变成下载 -->
        <iframe
          v-else-if="currentPreview?.kind === 'pdf'"
          class="preview-pdf"
          :src="currentPreview.previewUrl"
          :title="currentPreview.name"
        ></iframe>
        <!-- 这类文件浏览器看不了，只给下载；硬 inline 出来是一屏乱码 -->
        <div v-else class="preview-download-only">
          <b>{{ currentPreview?.name }}</b>
          <span>这个类型没法在浏览器里直接看，下载下来打开。</span>
          <a class="preview-download" :href="currentPreview?.downloadUrl">下载</a>
        </div>
        <figcaption>
          <span>{{ currentPreview?.name }}</span>
          <a
            v-if="currentPreview?.previewUrl"
            :href="currentPreview.previewUrl"
            target="_blank"
            rel="noopener"
          >在新标签打开</a>
          <a :href="currentPreview?.downloadUrl">下载</a>
          <button type="button" @click="preview = null">关闭</button>
        </figcaption>
        <!-- 一条问题传了好几张图时在这里换，不用退出去重点一次 -->
        <nav v-if="preview.files.length > 1" class="preview-strip">
          <button
            v-for="(file, at) in preview.files"
            :key="file.downloadUrl"
            type="button"
            :class="{ active: at === preview.index }"
            @click="preview.index = at"
          >{{ file.name }}</button>
        </nav>
      </figure>
    </div>
    <IssueEscalateDialog
      v-if="escalateItem"
      :issue="{ id: escalateItem.id, code: String(escalateItem.code || ''), title: String(escalateItem.title || '') }"
      :advice="adviceOf(escalateItem)"
      @close="escalateItem = null"
      @escalated="emit('escalated', $event)"
    />
  </section>
</template>

<style scoped>
.issue-project a { color: var(--blue); font-size: 12px; white-space: nowrap; }
.issue-project a:hover { text-decoration: underline; }
.issue-muted { color: var(--muted); font-size: 12px; }
/* 1027 起排在表后：自带内边距，不贴着表格最后一行 */
.dashboard-toggle { display: flex; align-items: center; gap: 10px; padding: 10px 12px; }

.dashboard-toggle > button {
  padding: 5px 12px;
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--blue);
  font: 700 12px var(--body);
  cursor: pointer;
}

.dashboard-toggle > button:hover { border-color: var(--blue); }

.drill-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  background: #EEF4FA;
  color: var(--blue);
  font-size: 12px;
}

.drill-clear { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--blue); }
.drill-clear:hover { border-color: var(--blue); background: rgba(0,117,174,.07); }

/* 缩略图：不用点开就知道问题长什么样，这是旧 EQS 那张表最实用的一点 */
/* 能点的东西必须有边框、看得出是按钮——徽章现在是「点开就能看附件」的入口 */
.attach-badge { display: inline-flex; align-items: center; gap: 2px; padding: 1px 7px; border: 1px solid var(--line-strong); border-radius: 999px; background: var(--white); color: var(--blue); font: 700 11px var(--body); cursor: pointer; }
.attach-badge:hover:not(:disabled) { border-color: var(--blue); background: rgba(0,117,174,.07); }
.attach-badge:disabled { cursor: progress; opacity: .6; }

/* 改不了的格子：压暗 + 光标改成默认，鼠标悬上去有 title 说明谁能改 */
table.can-edit td.cell-edit.locked { background: repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(16,39,56,.045) 5px, rgba(16,39,56,.045) 10px); cursor: default; }
table.can-edit td.cell-edit.locked:hover { background: repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(16,39,56,.045) 5px, rgba(16,39,56,.045) 10px); }
.thumb-cell { white-space: nowrap; }

.thumb {
  width: 34px;
  height: 34px;
  padding: 0;
  margin-right: 3px;
  border: 1px solid var(--line);
  background: var(--paper);
  overflow: hidden;
  vertical-align: middle;
  cursor: pointer;
}

.thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.thumb.is-file { display: inline-flex; align-items: center; justify-content: center; color: var(--muted); font: 700 9px var(--display); }
/* 证据附件用绿边和描述附件区分开——旧系统是分两列的 */
.thumb.evidence { border-color: var(--green); border-width: 2px; }
.thumb:hover { border-color: var(--blue); }
.thumb-cell small { color: var(--muted); font-size: 12px; }

.overdue-cell b { color: var(--risk); font: 700 14px var(--display); }
.overdue-cell span { color: var(--muted); }

/* 行底色：A 类和逾期一眼可辨，已关闭的压暗让位 */
tbody tr.grade-a { background: #FDF6F5; }
tbody tr.overdue td:first-child { box-shadow: inset 3px 0 var(--risk); }
tbody tr.is-closed { opacity: .72; }

.preview-mask {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(16, 39, 56, .72);
}

.preview-box { max-width: min(1100px, 92vw); max-height: 90vh; margin: 0; display: grid; gap: 8px; }
.preview-box .preview-pdf { width: min(1000px, 90vw); height: 78vh; border: 0; background: var(--white); }
.preview-download-only { display: grid; gap: 8px; justify-items: center; padding: 32px 40px; background: var(--white); text-align: center; }
.preview-download-only b { font: 700 15px var(--display); }
.preview-download-only span { color: var(--muted); font-size: 13px; }
.preview-download { padding: 5px 14px; border: 1px solid var(--blue); color: var(--blue); font-size: 13px; font-weight: 700; }
.preview-strip { display: flex; flex-wrap: wrap; gap: 6px; }
.preview-strip button { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 3px 10px; border: 1px solid rgba(255,255,255,.4); background: transparent; color: white; font-size: 12px; cursor: pointer; }
.preview-strip button.active { border-color: var(--cyan); color: var(--cyan); }
.preview-box img { max-width: 100%; max-height: 78vh; object-fit: contain; background: var(--white); }
.preview-box figcaption { display: flex; align-items: center; gap: 12px; color: white; font-size: 13px; }
.preview-box figcaption span { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.preview-box a { color: var(--cyan); }
.preview-box button { padding: 4px 12px; border: 1px solid rgba(255,255,255,.4); background: transparent; color: white; cursor: pointer; }

.escalate {
  display: inline-block;
  margin-left: 5px;
  padding: 0 5px;
  border: 1px solid var(--risk);
  color: var(--risk);
  font-size: 12px;
  font-weight: 700;
}

/* 升级层级：文字为主、颜色为辅（LOP G 列：灰 / 绿 / 黄 / 红） */
.level {
  display: inline-block;
  margin-left: 5px;
  padding: 1px 6px;
  border: 1px solid var(--line-strong);
  border-radius: 3px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
}
.level-green { color: #1b6b3a; background: #e6f6ec; border-color: #9ed4b2; }
.level-yellow { color: #8a5d00; background: #fff0c7; border-color: #f1cf6f; }
.level-red { color: var(--red); background: #ffe2df; border-color: #f2a39c; }
.level-grey { color: var(--muted); background: var(--surface); }
.escalate-button { border-color: var(--line-strong); }
.escalate-button.suggested { border-color: var(--risk); color: var(--risk); font-weight: 800; }
.quick-missing { border-color: var(--risk) !important; }


.age-cell b { font: 700 14px var(--display); }
.age-cell b.risk { color: var(--risk); }

/* 进展列容易撑爆表格：限宽 + 两行截断，完整日志在编辑抽屉里看 */
.progress-cell {
  max-width: 230px;
}

.progress-cell span {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.6;
}

.issue-workspace {
  border: 1px solid var(--line);
  background: var(--white);
}

.issue-workspace > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
}

.issue-workspace > header div {
  display: grid;
  gap: 5px;
}

.issue-workspace h2,
.issue-workspace p {
  margin: 0;
}

.issue-workspace h2 {
  color: var(--ink);
  font-size: 22px;
}

.issue-workspace p {
  color: var(--muted);
  font-size: 12px;
}

.create-issue {
  min-height: 42px;
  padding: 9px 14px;
  border: 1px solid var(--blue);
  color: white;
  background: var(--blue);
  cursor: pointer;
  font-weight: 800;
}

.assist-entry {
  background: var(--white);
  color: var(--blue);
}

.issue-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(130px, 1fr));
  gap: 8px;
  padding: 12px;
  background: var(--surface);
}

.issue-kpis button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 54px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  color: var(--muted);
  background: white;
  cursor: pointer;
  font-weight: 800;
}

.issue-kpis button:hover,
.issue-kpis button:focus-visible,
.issue-kpis button.active {
  border-color: var(--blue);
  color: var(--ink);
  background: rgba(0, 117, 174, .07);
  outline: none;
}

.issue-kpis strong {
  color: var(--blue);
  font: 800 22px var(--display);
}

.issue-sort select { min-width: 210px; }
.issue-filters {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) repeat(3, 150px) auto;
  gap: 9px;
  align-items: end;
  padding: 12px;
  /* 1027 起紧跟 header，header 自带下边线；再画上边线会成双线 */
  border-bottom: 1px solid var(--line);
}

.issue-filters label {
  display: grid;
  gap: 5px;
}

.issue-filters label > span {
  color: var(--muted);
  font-size: 10px;
  font-weight: 800;
}

.issue-filters input,
.issue-filters select {
  min-height: 40px;
  border: 1px solid var(--line);
  background: white;
}

.issue-filters > b {
  padding: 0 4px 11px;
  color: var(--blue);
  white-space: nowrap;
}

.issue-table-wrap {
  overflow-x: auto;
}

.issue-table-wrap table {
  width: 100%;
  min-width: 1220px;
  border-collapse: collapse;
  font-size: 12px;
}

.issue-table-wrap th {
  padding: 10px 9px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  background: #e9e6dc;
  text-align: left;
  white-space: nowrap;
}

.issue-table-wrap td {
  padding: 10px 9px;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
}

.issue-table-wrap tbody tr {
  cursor: pointer;
}

.issue-table-wrap tbody tr:hover,
.issue-table-wrap tbody tr:focus {
  outline: none;
  background: rgba(18, 109, 166, .06);
}

.issue-table-wrap tbody tr.overdue {
  box-shadow: inset 3px 0 var(--red);
}

.issue-table-wrap td strong,
.issue-table-wrap td small {
  display: block;
}

.issue-table-wrap td strong {
  margin-bottom: 4px;
  color: var(--ink);
  font-size: 12px;
}

.issue-table-wrap td small {
  max-width: 290px;
  color: var(--muted);
  line-height: 1.45;
}

.issue-table-wrap td button {
  padding: 6px 8px;
  border: 1px solid var(--blue);
  color: var(--blue);
  background: white;
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
}

.priority {
  display: inline-block;
  padding: 3px 7px;
  color: var(--muted);
  background: var(--surface);
  font-size: 10px;
  font-weight: 800;
}

.priority.high {
  color: #8a5d00;
  background: #fff0c7;
}

.priority.critical {
  color: var(--red);
  background: #ffe2df;
}

.missing,
.danger {
  color: var(--red);
  font-weight: 800;
}

.issue-empty {
  display: grid;
  justify-items: center;
  gap: 7px;
  width: 100%;
  padding: 46px 20px;
  border: 0;
  color: var(--blue);
  background: white;
  cursor: pointer;
}

.issue-empty span {
  color: var(--muted);
}

@media (max-width: 1000px) {
  .issue-kpis {
    grid-template-columns: repeat(2, minmax(130px, 1fr));
  }

  .issue-filters {
    grid-template-columns: 1fr 1fr;
  }

  .issue-search {
    grid-column: 1 / -1;
  }
}

@media (max-width: 600px) {
  .issue-workspace > header {
    align-items: stretch;
    flex-direction: column;
  }

  .issue-kpis,
  .issue-filters {
    grid-template-columns: 1fr;
  }

  .issue-search {
    grid-column: auto;
  }
}
/*
  就地编辑：改得最勤的六列点一下直接改，不用开抽屉。
  只给「点进去会被挤扁」的三列留 min-width：分类和等级不设的话
  下拉只有 79px / 61px，「工装问题」「A-严重」都显示不全。
  责任人、当前计划、状态本来就够宽，不用抢。

  col-desc 是**兜底**：这几个 min-width 抢的宽度全是从描述列身上来的，
  实测一度把它从 140px 压到 66px——最该看清的一列反而最窄。
*/
.col-desc { min-width: 176px; }
.col-category { min-width: 96px; }
.col-grade { min-width: 88px; }
.col-owner { min-width: 76px; }
.col-progress { min-width: 150px; }
/* 原因/措施：一格两行，各自单行截断——悬浮 title 看全文，改内容进抽屉 */
.col-cause { min-width: 160px; }
.cause-cell { max-width: 220px; }
.cause-line { display: flex; gap: 4px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-size: 12px; }
.cause-line i { flex: 0 0 auto; font-style: normal; color: var(--muted); }
.proposer-cell { white-space: nowrap; }
/* 状态都是两三个字，别让「已逾期」「处理中」折成两行（1027） */
.col-status { white-space: nowrap; }

table.can-edit .cell-edit { cursor: text; }

table.can-edit .cell-edit:hover {
  background: rgba(0, 117, 174, .07);
  box-shadow: inset 0 0 0 1px rgba(0, 117, 174, .3);
}

.cell-edit.is-editing {
  background: var(--white);
  box-shadow: inset 0 0 0 2px var(--blue);
  vertical-align: middle;
}

.cell-edit input,
.cell-edit select {
  width: 100%;
  min-height: 30px;
  padding: 0 6px;
  border: 1px solid var(--line);
  border-radius: 3px;
  font: inherit;
  font-size: 12px;
}

.cell-edit input:focus,
.cell-edit select:focus {
  border-color: var(--blue);
  outline: none;
}

/* 底部快速加一行：视觉上要像表格的一部分，但一眼看得出是「还没保存的新行」 */
.issue-quick-row td { background: #fbfcfe; border-top: 2px dashed var(--line); vertical-align: middle; }
.issue-quick-row input, .issue-quick-row select {
  width: 100%; min-height: 32px; padding: 0 8px;
  border: 1px solid var(--line); border-radius: 4px; font: inherit; font-size: 12px;
}
.issue-quick-row input:focus, .issue-quick-row select:focus { border-color: var(--blue); outline: none; }
.quick-hint { color: var(--blue); font-size: 12px; font-weight: 800; white-space: nowrap; }
.quick-muted { color: var(--muted); font-size: 11px; }
.quick-save {
  padding: 5px 10px; border: 1px solid var(--blue); border-radius: 4px;
  background: var(--blue); color: #fff; font: 700 12px var(--body); cursor: pointer; white-space: nowrap;
}
.quick-save:disabled { opacity: .45; cursor: not-allowed; }
</style>
